import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, BrowserRouter } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import { ROUTES } from "./constants/frontendPaths";
import { ResendProvider } from "./components/Context/ResendContext";
import { ApprovalProvider } from "./components/Context/ApprovalContext";
import LocalStorageProvider from "./components/Provider/LocalStorageProvider";
import AutoUpdateToken from "./services/Account/AutoUpdaetToken";
import AuthRoleGuard from "./guards/AuthRoleGuard";
import Spin from "antd/es/spin";
import usePageTracking from "./hooks/usePageTracking";

// Lazy loaded components - Auth Pages
const Login = lazy(() => import("./components/Account/Login/Login"));
const Register = lazy(() => import("./components/Account/Register/Register"));
const ForgotPassword = lazy(() => import("./components/Account/ForgotPassword/ForgotPassword"));
const SetPassword = lazy(() => import("./components/Account/SetPassword/SetPassword"));
const ResetPassword = lazy(() => import("./components/Account/ResetPassword/ResetPassword"));
const SetMFA = lazy(() => import("./components/Account/SetPassword/SetMFA"));
const InvoiceCreatorRegister = lazy(() => import("./components/Account/InvoiceRegister/InvoiceCreatorRegister"));
const InvoicePayerRegister = lazy(() => import("./components/Account/InvoiceRegister/InvoicePayerRegister"));
// const InvoiceLogin = lazy(() => import("./components/Account/InvoiceLogin/InvoiceLogin"));
const VerifyEmailPage = lazy(() => import("./components/Account/VerifyEmailPage/VerifyEmailPage"));
const TwoFactorSetupPage = lazy(() => import("./components/Account/TwoFactorSetupPage/TwoFactorSetupPage"));
const VerifyIvaltLoginPage = lazy(() => import("./components/Account/VerifyIvaltLoginPage/VerifyIvaltLoginPage"));

// Lazy loaded components - Dashboard Layout & Protected Routes
const DashboardLayout = lazy(() => import("./Layout/DashboardLayout"));
const Dashboard = lazy(() => import("./Layout/Dashboard"));
const Details = lazy(() => import("./Layout/Details"));
const Payment = lazy(() => import("./Layout/Payment"));
const PaymentConfiguration = lazy(() => import("./Layout/PaymentConfiguration"));
const CreatePaymentv2 = lazy(() => import("./components/PartnerOnboardReady/Payment/CreatePayment/CreatePayment/CreatePayment"));
const TransactionDetails = lazy(() => import("./components/PartnerOnboardReady/Payment/TransactionDetails/TransactionDetails"));
// Lazy loaded components - Partner Components
const OnboardingDialogue = lazy(() => import("./components/Partner/OnbordingDialogue"));
const Paymentsettingread = lazy(() => import("./components/Partner/PaymentReadonly/Paymentsettingread"));
const BulkPayment = lazy(() => import("./components/Partner/BulkPayment/BulkPayment"));
const BulkPaymentSpreadsheet = lazy(() => import("./components/Partner/BulkPayment/BulkPaymentSpreadsheet"));
const BulkPaymentList = lazy(() => import("./components/Partner/BulkPayment/BulkPaymentList"));
const FraudScreen = lazy(() => import("./components/Partner/FraudScreen/FraudScreen"));
const BatchResendDetails = lazy(() => import("./components/Partner/BulkPaymentResend/BatchResendDetails"));
const BatchConfirm = lazy(() => import("./components/Partner/BulkPaymentResend/BatchConfirm"));
const InvoiceList = lazy(() => import("./components/Partner/InvoiceList/InvoiceList"));
const ComplainceTab = lazy(() => import("./components/Partner/Compliances/ComplianceTab"));
const Errorpage = lazy(() => import("./components/Partner/ErrorPage/Errorpage"));

// Lazy loaded components - PartnerOnboardReady
const CreatePayment = lazy(() => import("./components/PartnerOnboardReady/Payment/CreatePayment"));
const Transactions = lazy(() => import("./components/PartnerOnboardReady/Transaction/Transactions"));
const Users = lazy(() => import("./components/PartnerOnboardReady/Users/Users"));
const CheckQuotes = lazy(() => import("./components/PartnerOnboardReady/CheckQuotes/CheckQuotes"));
const DeveloperResources = lazy(() => import("./components/PartnerOnboardReady/DeveloperResources/DeveloperResources"));
const FxRate = lazy(() => import("./components/PartnerOnboardReady/FxRate/FxRate"));
const Reports = lazy(() => import("./components/PartnerOnboardReady/Reports/Reports"));
const FacilitatorTabContent = lazy(() => import("./components/PartnerOnboardReady/Facilitator/FacilitatorTabcontent"));
const SubmitPayment = lazy(() => import("./components/PartnerOnboardReady/Payment/SubmitPayment"));
const TransactionsPaymentDetails = lazy(() => import("./components/PartnerOnboardReady/Transaction/TransactionPaymentDetails"));
const Ledger = lazy(() => import("./components/PartnerOnboardReady/Ledger/Ledger"));
const AdditionalPaymentFields = lazy(() => import("./components/PartnerOnboardReady/Payment/AdditionalPaymentFields"));
const StaticCreatePayment = lazy(() => import("./components/PartnerOnboardReady/Payment/StaticCreatePayment"));

// Lazy loaded components - Account Profile
const Profile = lazy(() => import("./components/Account/Profile/Profile"));
const ProfileDetails = lazy(() => import("./components/Account/Profile/ProfileDetails"));

// Lazy loaded components - Invoice Creator
const InvoiceCreatorBusinessProfile = lazy(() => import("./components/Account/InvoiceCreatorBusinessProfile/InvoiceCreatorBusinessProfile"));
const CreatorInvoiceList = lazy(() => import("./components/Invoice/Creator/CreatorInvoiceList/CreatorInvoiceList"));
const InvoiceChoiceModal = lazy(() => import("./components/Invoice/Creator/CreateInvoicePage/InvoiceChoiceModal"));
const CreateInvoicePage = lazy(() => import("./components/Invoice/Creator/CreateInvoicePage/CreateInvoicePage"));
const UploadInvoicePage = lazy(() => import("./components/Invoice/Creator/CreateInvoicePage/UploadInvoicePage/UploadInvoicePage"));
const PendingApproval = lazy(() => import("./components/Account/InvoiceCreatorBusinessProfile/PendingApproval/PendingApproval"));
const CreatorDashboard = lazy(() => import("./components/Invoice/Creator/CreatorDashboard/CreatorDashboard"));

// Lazy loaded components - Invoice Payer
const PayerInvoiceList = lazy(() => import("./components/Invoice/Payer/PayerInvoiceList/PayerInvoiceList"));
const InvoiceReviewPage = lazy(() => import("./components/Invoice/Payer/InvoiceReviewCard/InvoiceReviewPage"));
const PayerDashboard = lazy(() => import("./components/Invoice/Payer/PayerDashboard/PayerDashboard"))

// Lazy loaded components - Risk Profile
const RiskProfile = lazy(() => import("./components/Account/RiskProfile/RiskProfile"));

// Loading fallback component
const LoadingFallback = () => (
	<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
		<Spin size="large" />
	</div>
);
const PageTrackingWrapper = (): JSX.Element | null => {
	usePageTracking();
	return null;
};

function App() {
	const Env1 = sessionStorage.getItem("Env");
	// const isDev = process.env.Env1 === "Dev";

	// const location = useLocation();

	// useEffect(() => {
	//   if (location.pathname !== '/createpayment') {
	//     // sessionStorage.clear();
	//     sessionStorage.removeItem('RoleType');
	//   }
	// }, [location.pathname]);

	return (
		<BrowserRouter>
			<ResendProvider>
				<ApprovalProvider>
					<LocalStorageProvider>
						<AutoUpdateToken />
						<PageTrackingWrapper />
						<Routes>
							<Route path={ROUTES.home} element={<Suspense fallback={<LoadingFallback />}><Login /></Suspense>} />
							<Route path={ROUTES.login} element={<Navigate to={ROUTES.home} />} />
							<Route path={ROUTES.register} element={<Suspense fallback={<LoadingFallback />}><Register /></Suspense>} />
							<Route path={ROUTES.forgotPassword} element={<Suspense fallback={<LoadingFallback />}><ForgotPassword /></Suspense>} />
							<Route path={ROUTES.setPassword} element={<Suspense fallback={<LoadingFallback />}><SetPassword /></Suspense>} />
							<Route path={ROUTES.resetPassword} element={<Suspense fallback={<LoadingFallback />}><ResetPassword /></Suspense>} />
							<Route path={ROUTES.setMfa} element={<Suspense fallback={<LoadingFallback />}><SetMFA /></Suspense>} />
							<Route path={ROUTES.invoiceCreatorRegister} element={<Suspense fallback={<LoadingFallback />}><InvoiceCreatorRegister /></Suspense>} />
							<Route path={ROUTES.invoicePayerRegister} element={<Suspense fallback={<LoadingFallback />}><InvoicePayerRegister /></Suspense>} />
							<Route path={ROUTES.invoiceLogin} element={<Navigate to="/" replace />} />
							<Route path={ROUTES.verifyOtp} element={<Suspense fallback={<LoadingFallback />}><VerifyEmailPage /></Suspense>} />
							<Route path={ROUTES.ivaltInstruction} element={<Suspense fallback={<LoadingFallback />}><TwoFactorSetupPage /></Suspense>} />
							<Route path={ROUTES.verifyIvalt} element={<Suspense fallback={<LoadingFallback />}><VerifyIvaltLoginPage /></Suspense>} />

							<Route
								path={ROUTES.home}
								element={
									<Suspense fallback={<LoadingFallback />}>
										<DashboardLayout />
									</Suspense>
								}
							>
								<Route
									path={ROUTES.dashboard}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Dashboard />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									index
									path={ROUTES.details}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Details />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.payment}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Payment />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.onboarding}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<OnboardingDialogue />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.paymentSettingRead}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Paymentsettingread />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								{/* <Route
									path={ROUTES.createPayment}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<CreatePayment />
											</Suspense>
										</AuthRoleGuard>
									}
								/> */}
								<Route
									path={ROUTES.bulkPayment}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<BulkPayment />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.createPaymentTest}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<StaticCreatePayment />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.addPaymentFields}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<AdditionalPaymentFields />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.submitPayment}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<SubmitPayment />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.bulkPaymentSpreadsheet}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<BulkPaymentSpreadsheet />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.bulkPaymentList}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<BulkPaymentList />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.transactions}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Transactions />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.users}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Users />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.fraudScreen}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<FraudScreen />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.checkQuotes}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<CheckQuotes />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.facilitator}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<FacilitatorTabContent />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.developerResources}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<DeveloperResources />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.fxRate}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<FxRate />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.reports}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Reports />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.profile}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Profile />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.batchResend}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<BatchResendDetails />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.batchResendConfirm}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<BatchConfirm />
											</Suspense>
										</AuthRoleGuard>
									}
								/>

								<Route
									path={ROUTES.profileDetails}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<ProfileDetails />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.paymentConfiguration}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<PaymentConfiguration />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.ledger}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<Ledger />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								{/* <Route
									path={ROUTES.transactionDetails}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<TransactionsPaymentDetails />
											</Suspense>
										</AuthRoleGuard>
									}
								/> */}
								<Route
									path={ROUTES.transactionDetails}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<TransactionDetails />
											</Suspense>
										</AuthRoleGuard>
									}
								/>

								<Route
									path={ROUTES.invoiceList}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<InvoiceList />
											</Suspense>
										</AuthRoleGuard>
									}
								/>

								<Route
									path={ROUTES.adminCompliance}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3, 6]}>
											<Suspense fallback={<LoadingFallback />}>
												<ComplainceTab />
											</Suspense>
										</AuthRoleGuard>
									}
								/>


								<Route
									path={ROUTES.creatorDashboard}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<CreatorDashboard />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.creatorProfile}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<InvoiceCreatorBusinessProfile />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.creatorInvoiceList}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<CreatorInvoiceList />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.creatorInvoice}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<InvoiceChoiceModal />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.manualCreateInvoice}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<CreateInvoicePage />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.uploadInvoice}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<UploadInvoicePage />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.pendingApproval}
									element={
										<AuthRoleGuard allowedRoleIds={[8]}>
											<Suspense fallback={<LoadingFallback />}>
												<PendingApproval />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.payerDashboard}
									element={
										<AuthRoleGuard allowedRoleIds={[9]}>
											<Suspense fallback={<LoadingFallback />}>
												<PayerDashboard />
											</Suspense>
										</AuthRoleGuard>
									}
								/>

								<Route
									path={ROUTES.payerInvoiceList}
									element={
										<AuthRoleGuard allowedRoleIds={[9]}>
											<Suspense fallback={<LoadingFallback />}>
												<PayerInvoiceList />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
								<Route
									path={ROUTES.payerInvoiceReview}
									element={
										<AuthRoleGuard allowedRoleIds={[9]}>
											<Suspense fallback={<LoadingFallback />}>
												<InvoiceReviewPage />
											</Suspense>
										</AuthRoleGuard>
									}
								/>

								<Route
									path={ROUTES.riskProfile}
									element={
										<Suspense fallback={<LoadingFallback />}>
											<RiskProfile />
										</Suspense>
									}
								/>

								<Route
									path={ROUTES.createPayment}
									element={
										<AuthRoleGuard allowedRoleIds={[2, 3]}>
											<Suspense fallback={<LoadingFallback />}>
												<CreatePaymentv2 />
											</Suspense>
										</AuthRoleGuard>
									}
								/>
							</Route>

							<Route path="*" element={<Suspense fallback={<LoadingFallback />}><Errorpage /></Suspense>} />
						</Routes>
					</LocalStorageProvider>
				</ApprovalProvider>
			</ResendProvider>
		</BrowserRouter>
	);
}

export default App;
